import { ChatMessageKind } from "~/shared/enums";
import type {
  AssistantTextMessage,
  ChatMessage,
  DraftMessage,
  MessageFrag,
  TextMessage,
  ThinkingMessage,
  ToolMessage,
  UserTextMessage,
} from "~/shared/types";
import { getItemCountText } from "~/utils/common";

type StepMessage = ThinkingMessage | ToolMessage;

const TOOL_STEP_MESSAGE_KINDS = new Set<ChatMessageKind>([
  ChatMessageKind.TOOL_CALL,
  ChatMessageKind.TOOL_RESULT,
  ChatMessageKind.TOOL_ERROR,
]);

const STEP_MESSAGE_KINDS = new Set<ChatMessageKind>([ChatMessageKind.THINKING, ...TOOL_STEP_MESSAGE_KINDS]);

export const isToolStepMessageKind = (kind: ChatMessageKind): boolean => TOOL_STEP_MESSAGE_KINDS.has(kind);
export const isStepMessageKind = (kind: ChatMessageKind): boolean => STEP_MESSAGE_KINDS.has(kind);
export const isAssistantTextMessage = (message: ChatMessage): message is AssistantTextMessage =>
  message.kind === ChatMessageKind.ASSISTANT_TEXT;
export const isUserTextMessage = (message: ChatMessage): message is UserTextMessage =>
  message.kind === ChatMessageKind.USER_TEXT;
export const isTextMessage = (message: ChatMessage): message is TextMessage =>
  message.kind === ChatMessageKind.USER_TEXT || message.kind === ChatMessageKind.ASSISTANT_TEXT;
export const isDraftMessage = (message: ChatMessage): message is DraftMessage => message.kind === ChatMessageKind.DRAFT;

const getHandledToolCallIds = (messages: ChatMessage[]): Set<string> =>
  new Set(
    messages.flatMap((message) =>
      message.kind === ChatMessageKind.TOOL_RESULT || message.kind === ChatMessageKind.TOOL_ERROR
        ? [message.toolCallId]
        : []
    )
  );

const filterRenderableMessages = <T extends ChatMessage>(messages: T[]): T[] => {
  const handledToolCallIds = getHandledToolCallIds(messages);
  return messages.filter(
    (message) =>
      (message.kind !== ChatMessageKind.TOOL_CALL || !handledToolCallIds.has(message.toolCallId)) &&
      message.kind !== ChatMessageKind.TOOL_ERROR
  );
};

export const filterRenderableChatMessages = (messages: ChatMessage[]): ChatMessage[] =>
  filterRenderableMessages(messages);

export const filterStepMessages = (messages: StepMessage[]): StepMessage[] => filterRenderableMessages(messages);

export const getStepSummaryFrags = (message: StepMessage): MessageFrag[] => {
  if (message.kind === ChatMessageKind.THINKING) {
    return message.summary ? [message.summary] : [];
  }
  return message.summary;
};

const getDefaultStepSubsectionTitleFrags = (messages: StepMessage[], isWorking: boolean): MessageFrag[] => {
  const count = filterStepMessages(messages).length;
  const isThinkingSubsection = messages[messages.length - 1]?.kind === ChatMessageKind.THINKING;
  if (isThinkingSubsection) {
    return [`${isWorking ? "Having" : "Had"} ${getItemCountText(count, "thought")}`];
  }
  if (isWorking) {
    return [`Doing ${getItemCountText(count, "action")}`];
  }
  return [`Performed ${getItemCountText(count, "action")}`];
};

export const getStepSubsectionTitleFrags = (messages: StepMessage[], isWorking: boolean): MessageFrag[] => {
  if (!isWorking) {
    return getDefaultStepSubsectionTitleFrags(messages, false);
  }

  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) {
    return ["Working"];
  }

  const frags = getStepSummaryFrags(lastMessage);
  if (frags.length > 0) {
    return frags;
  }

  return getDefaultStepSubsectionTitleFrags(messages, true);
};

export const isStepSectionHidden = (messages: StepMessage[], isWorking: boolean): boolean => {
  if (messages.length === 0) {
    return true;
  }

  const lastMessageKind = messages[messages.length - 1].kind;
  return filterStepMessages(messages).length === 0 && !isWorking && lastMessageKind !== ChatMessageKind.THINKING;
};
